import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Input, Switch, message, Row, Col, Divider, Tabs, Select, Spin } from "antd";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import createSellerMutation from "./queries/createSellerMutation";
import availableOrgsQuery from "./queries/availableOrgsQuery";
import enableMarketplaceMutation from "./queries/enableMarketplaceMutation";

const CreateSellerModal = ({ visible, onCancel, onCreate }) => {
  const [manualForm] = Form.useForm();
  const [orgForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("manual");
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      // Fetch organizations where marketplace is not true
      const where = {
        OR: [
          { marketplace: { equalTo: false } },
          { marketplace: { exists: false } }
        ]
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: availableOrgsQuery,
          variables: {
            first: 1000,
            where
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.organizzaziones?.edges || [];
      setOrganizations(edges.map((edge) => edge.node));
    } catch (error) {
      console.error("Error fetching organizations:", error);
      message.error("Errore durante il caricamento delle organizzazioni");
    } finally {
      setOrgsLoading(false);
    }
  }, [getTokenSilently, env]);

  useEffect(() => {
    if (visible && activeTab === "fromOrg") {
      fetchOrganizations();
    }
  }, [visible, activeTab, fetchOrganizations]);

  const handleCreate = async () => {
    try {
      const currentForm = activeTab === "manual" ? manualForm : orgForm;
      const values = await currentForm.validateFields();
      setLoading(true);

      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      if (activeTab === "manual") {
        // Manual creation
        const fields = {
          name: values.name,
          phone: values.phone || null,
          enabled: values.enabled !== undefined ? values.enabled : true,
          public: values.public !== undefined ? values.public : false,
          b2b: values.b2b !== undefined ? values.b2b : false,
          b2c: values.b2c !== undefined ? values.b2c : false,
          public_prices: values.public_prices !== undefined ? values.public_prices : false,
          ask_for_info: values.ask_for_info !== undefined ? values.ask_for_info : false,
        };

        await axios.post(
          graphqlUrl,
          {
            query: createSellerMutation,
            variables: { fields },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        message.success("Venditore creato con successo");
      } else {
        // Create from organization (enable marketplace)
        // Step 1: Get the organization details
        const orgQuery = `
          query GetOrg($id: ID!) {
            organizzazione(id: $id) {
              objectId
              nome
              telefono1
              cellulare1
            }
          }
        `;

        const orgResponse = await axios.post(
          graphqlUrl,
          {
            query: orgQuery,
            variables: { id: values.orgId },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const org = orgResponse.data?.organizzazione;

        if (!org) {
          throw new Error("Organizzazione non trovata");
        }

        // Step 2: Enable marketplace on the organization
        await axios.post(
          graphqlUrl,
          {
            query: enableMarketplaceMutation,
            variables: {
              id: values.orgId,
              fields: {
                marketplace: true
              }
            },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Step 3: Create the marketplace_seller record
        const sellerFields = {
          name: org.nome,
          phone: org.telefono1 || org.cellulare1 || null,
          enabled: true,
          public: false,
          b2b: false,
          b2c: false,
          public_prices: false,
          ask_for_info: false,
          org_pointer: {
            link: org.objectId,
          },
        };

        await axios.post(
          graphqlUrl,
          {
            query: createSellerMutation,
            variables: { fields: sellerFields },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        message.success("Organizzazione aggiunta al marketplace");
      }

      manualForm.resetFields();
      orgForm.resetFields();
      onCreate();
    } catch (error) {
      if (error.errorFields) {
        // Validation error from form
        return;
      }
      console.error("Error creating seller:", error);
      message.error("Errore durante la creazione del venditore");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    manualForm.resetFields();
    orgForm.resetFields();
    setActiveTab("manual");
    onCancel();
  };

  const tabItems = [
    {
      key: "manual",
      label: "Creazione Manuale",
      children: (
        <Form
          form={manualForm}
          layout="vertical"
          initialValues={{
            enabled: true,
            public: false,
            b2b: false,
            b2c: false,
            public_prices: false,
            ask_for_info: false,
          }}
        >
          <Divider orientation="left">Informazioni Generali</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Nome"
                name="name"
                rules={[{ required: true, message: "Il nome è obbligatorio" }]}
              >
                <Input placeholder="Nome del venditore" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Telefono"
                name="phone"
              >
                <Input placeholder="Numero di telefono" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Impostazioni di Visibilità</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Attivo"
                name="enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Pubblico"
                name="public"
                valuePropName="checked"
                tooltip="Se attivo, il profilo sarà visibile pubblicamente"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Prezzi Pubblici"
                name="public_prices"
                valuePropName="checked"
                tooltip="Se attivo, i prezzi saranno visibili pubblicamente"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Servizi Offerti</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="B2B"
                name="b2b"
                valuePropName="checked"
                tooltip="Indica se è un fornitore di prodotti/servizi B2B"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="B2C"
                name="b2c"
                valuePropName="checked"
                tooltip="Indica se offre servizi funebri completi B2C"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Interazione con Clienti</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Richiesta Informazioni"
                name="ask_for_info"
                valuePropName="checked"
                tooltip="Se attivo, i clienti possono richiedere informazioni"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ),
    },
    {
      key: "fromOrg",
      label: "Crea a partire da Organizzazione Eterno",
      children: (
        <Form form={orgForm} layout="vertical">
          <p style={{ marginBottom: 16, color: "#666" }}>
            Seleziona un'organizzazione Eterno per abilitare il marketplace.
            Il venditore corrispondente verrà creato automaticamente dal database.
          </p>

          <Form.Item
            label="Organizzazione Eterno"
            name="orgId"
            rules={[{ required: true, message: "Seleziona un'organizzazione" }]}
          >
            <Select
              showSearch
              placeholder="Cerca e seleziona un'organizzazione"
              loading={orgsLoading}
              notFoundContent={orgsLoading ? <Spin size="small" /> : "Nessuna organizzazione disponibile"}
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={organizations.map((org) => ({
                label: `${org.nome}${org.citta ? ` (${org.citta})` : ""} - ${org.objectId}`,
                value: org.objectId,
              }))}
            />
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      title="Crea Nuovo Venditore"
      open={visible}
      onOk={handleCreate}
      onCancel={handleCancel}
      okText="Crea"
      cancelText="Annulla"
      confirmLoading={loading}
      width={700}
      maskClosable={false}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Modal>
  );
};

export default CreateSellerModal;
